import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Card,
  TextField,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDownwardIcon from '@mui/icons-material/KeyboardArrowDown';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Close'; // Mockup uses X icon
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  ApiGetBriefList,
  ApiGetBriefTitleList,
  ApiAddBrief,
  ApiUpdateBrief,
  ApiDeleteBrief,
  ApiGetClientEntities,
  ApiSaveBriefHeader,
  ApiUpdateNewBriefStatus,
  ApiUpdateBriefDisplayOrder
} from '../../../apiService/Core/Apicall.tsx';
import { setLoadingFalse, setLoadingTrue } from '../../../global/config/Action/loadingActions.ts';
import store from '../../../global/config/store.ts';
import PatchedReactQuill from '../ManageVenue/PatchedReactQuill.tsx';
import QuizSection from './QuizSection.tsx';

interface BriefIcon {
  briefIconId: number;
  iconName: string;
  iconImage: string;
}

interface BriefTitle {
  briefTitleId: number;
  title: string;
  kicker?: string;
  defaultDescription: string;
  isActive: boolean;
  briefIconId: BriefIcon;
  toneColor?: string;
}

interface BusinessBrief {
  businessBriefId: number;
  briefDescription: string;
  isNeedToUseDefaultDescription: boolean;
  displayOrder: number;
  briefTitleId: BriefTitle;
}

const BusinessBriefList: React.FC = () => {
  const { clientEntityId } = useParams<{ clientEntityId: string }>();
  const navigate = useNavigate();
  const currentUserId = Number(localStorage.getItem('userId') || localStorage.getItem('createdBy') || 0);

  const [briefs, setBriefs] = useState<BusinessBrief[]>([]);
  const [titles, setTitles] = useState<BriefTitle[]>([]);
  const [businessName, setBusinessName] = useState<string>('');
  const [mainTitle, setMainTitle] = useState<string>('');
  const [openMainTitleDialog, setOpenMainTitleDialog] = useState<boolean>(false);
  const [mainTitleInput, setMainTitleInput] = useState<string>('');

  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [selectedBrief, setSelectedBrief] = useState<BusinessBrief | null>(null);
  const [editorVal, setEditorVal] = useState<string>('');
  const [enableNewBrief, setEnableNewBrief] = useState<boolean>(false)
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [isChangeOrder, setIsChangeOrder] = useState(false)


  const fetchBusinessDetails = async () => {
    try {
      const response = await ApiGetClientEntities(`?clientEntityId=${clientEntityId}`);
      const data = response?.data?.[0] || response?.data || null;
      if (data) {
        setBusinessName(data.entityName);
        setEnableNewBrief(data?.isBusinessNewBriefEnabled)
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
    }
  };

  const fetchBriefs = async () => {
    if (!clientEntityId) return;
    try {
      store.dispatch(setLoadingTrue());
      const response = await ApiGetBriefList(`?type=business&clientEntityId=${clientEntityId}`);
      const data = response?.data || response || [];
      // Sort briefs by displayOrder
      const sorted = [...data].sort((a, b) => a.displayOrder - b.displayOrder);
      setBriefs(sorted);
      setMainTitle(response?.mainTitle || '');
    } catch (error: any) {
      console.error('Error fetching business briefs:', error);
      toast.error(error || 'Failed to fetch briefs');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  const fetchAvailableBriefTitles = async () => {
    try {
      const response = await ApiGetBriefTitleList('?isActive=true');
      const data = response?.data || response || [];
      setTitles(data);
    } catch (error) {
      console.error('Error fetching brief titles:', error);
    }
  };

  const handleSaveMainTitle = async () => {
    try {
      store.dispatch(setLoadingTrue());
      const payload = {
        type: 'business',
        clientEntityId: Number(clientEntityId),
        mainTitle: mainTitleInput,
        createdBy: currentUserId
      };
      await ApiSaveBriefHeader(payload);
      toast.success('Brief main title saved successfully');
      setOpenMainTitleDialog(false);
      fetchBriefs();
    } catch (error: any) {
      toast.error(error || 'Failed to save main title');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  useEffect(() => {
    fetchBusinessDetails();
    fetchBriefs();
    fetchAvailableBriefTitles();
  }, [clientEntityId]);

  const handleAddBriefTitle = async (titleId: number) => {
    try {
      store.dispatch(setLoadingTrue());
      const maxOrder = briefs.reduce((max, b) => Math.max(max, b.displayOrder), 0);
      const payload = {
        type: 'business',
        clientEntityId: Number(clientEntityId),
        briefTitleId: titleId,
        briefDescription: '',
        isNeedToUseDefaultDescription: true,
        displayOrder: maxOrder + 1,
        createdBy: currentUserId
      };

      const response = await ApiAddBrief(payload);
      toast.success(response.message || 'Section added to business successfully');
      setOpenAddDialog(false);
      fetchBriefs();
    } catch (error: any) {
      toast.error(error || 'Failed to add section');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  const handleEditClick = (brief: BusinessBrief) => {
    setSelectedBrief(brief);
    setEditorVal(brief.briefDescription || '');
    setOpenEditDialog(true);
  };

  const handleDeleteClick = async (briefId: number) => {
    const isConfirmed = window.confirm('Are you sure you want to remove this section from this business?');
    if (isConfirmed) {
      try {
        store.dispatch(setLoadingTrue());
        const payload = {
          type: 'business',
          businessBriefId: briefId
        };
        const response = await ApiDeleteBrief(payload);
        toast.success(response.message || 'Section removed successfully');
        fetchBriefs();
      } catch (error: any) {
        toast.error(error || 'Failed to remove section');
      } finally {
        store.dispatch(setLoadingFalse());
      }
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    try {
      store.dispatch(setLoadingTrue());
      const current = briefs[index];
      const previous = briefs[index - 1];

      // Swap displayOrder values
      const payload1 = {
        type: 'business',
        businessBriefId: current.businessBriefId,
        briefDescription: current.briefDescription,
        isNeedToUseDefaultDescription: current.isNeedToUseDefaultDescription,
        displayOrder: previous.displayOrder,
        modifiedBy: currentUserId
      };

      const payload2 = {
        type: 'business',
        businessBriefId: previous.businessBriefId,
        briefDescription: previous.briefDescription,
        isNeedToUseDefaultDescription: previous.isNeedToUseDefaultDescription,
        displayOrder: current.displayOrder,
        modifiedBy: currentUserId
      };

      await ApiUpdateBrief(payload1);
      await ApiUpdateBrief(payload2);
      fetchBriefs();
    } catch (error: any) {
      toast.error(error || 'Failed to reorder sections');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === briefs.length - 1) return;
    try {
      store.dispatch(setLoadingTrue());
      const current = briefs[index];
      const next = briefs[index + 1];

      // Swap displayOrder values
      const payload1 = {
        type: 'business',
        businessBriefId: current.businessBriefId,
        briefDescription: current.briefDescription,
        isNeedToUseDefaultDescription: current.isNeedToUseDefaultDescription,
        displayOrder: next.displayOrder,
        modifiedBy: currentUserId
      };

      const payload2 = {
        type: 'business',
        businessBriefId: next.businessBriefId,
        briefDescription: next.briefDescription,
        isNeedToUseDefaultDescription: next.isNeedToUseDefaultDescription,
        displayOrder: current.displayOrder,
        modifiedBy: currentUserId
      };

      await ApiUpdateBrief(payload1);
      await ApiUpdateBrief(payload2);
      fetchBriefs();
    } catch (error: any) {
      toast.error(error || 'Failed to reorder sections');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  // Formik validation for editing brief description
  const validationSchema = Yup.object({
    isNeedToUseDefaultDescription: Yup.boolean(),
    briefDescription: Yup.string().when('isNeedToUseDefaultDescription', {
      is: false,
      then: (schema) => schema.required('Brief Description is required when not using default description'),
      otherwise: (schema) => schema.optional()
    })
  });
  const onSubmit = async (values: { isNeedToUseDefaultDescription: boolean; briefDescription: string }) => {
    if (!selectedBrief) return;
    try {
      store.dispatch(setLoadingTrue());
      const payload = {
        type: 'business',
        businessBriefId: selectedBrief.businessBriefId,
        briefDescription: values.isNeedToUseDefaultDescription ? '' : values.briefDescription,
        isNeedToUseDefaultDescription: values.isNeedToUseDefaultDescription,
        displayOrder: selectedBrief.displayOrder,
        modifiedBy: currentUserId
      };

      const response = await ApiUpdateBrief(payload);
      toast.success(response.message || 'Brief updated successfully');
      setOpenEditDialog(false);
      fetchBriefs();
    } catch (error: any) {
      toast.error(error || 'Failed to update brief');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  const formik = useFormik({
    initialValues: {
      isNeedToUseDefaultDescription: selectedBrief?.isNeedToUseDefaultDescription ?? true,
      briefDescription: selectedBrief?.briefDescription || ''
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit
  });

  const handleEditorValueChange = (val: string) => {
    setEditorVal(val);
    formik.setFieldValue('briefDescription', val);
  };

  // Strip HTML utility to show plain text preview
  const stripHtml = (htmlStr: string) => {
    if (!htmlStr) return '';
    const doc = new DOMParser().parseFromString(htmlStr, 'text/html');
    const text = doc.body.textContent || '';
    return text.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  };

  // Filter out brief titles already added to the business
  const availableTitles = titles.filter(
    (t) => !briefs.some((b) => b.briefTitleId?.briefTitleId === t.briefTitleId)
  );

  const handleOpenNewBrief = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;

    try {
      const data = {
        type: "business",
        isEnable: checked,
        clientEntityId,
      };

      const res = await ApiUpdateNewBriefStatus(data);

      if (res?.status) {
        setEnableNewBrief(checked);
      }
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const handleOrderChange = (id: number, newOrder: number) => {
    setBriefs((prev) => {
      // Sort first to make sure the current order is correct
      const sorted = [...prev].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );

      // Find the item being moved
      const currentIndex = sorted.findIndex(
        (item) => item.businessBriefId === id
      );

      if (currentIndex === -1) {
        return prev;
      }

      // Remove item from current position
      const [movedItem] = sorted.splice(currentIndex, 1);

      // Keep new order within valid range
      const newIndex = Math.max(
        0,
        Math.min(newOrder - 1, sorted.length)
      );

      // Insert at new position
      sorted.splice(newIndex, 0, movedItem);

      // Recalculate displayOrder
      const reordered = sorted.map((item, index) => ({
        ...item,
        displayOrder: index + 1,
      }));

      return reordered;
    });

    // Scroll/highlight moved item
    setTimeout(() => {
      const element = document.getElementById(`brief-${id}`);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        element.classList.add('blink-highlight');

        setTimeout(() => {
          element.classList.remove('blink-highlight');
        }, 500);
      }
    }, 1000);
  };

  const saveDispalyOrder = async () => {
    try {
      const senddata = {
        type: "business",
        briefs: briefs?.map((item) => ({
          businessBriefId: item?.businessBriefId,
          displayOrder: item?.displayOrder,
        }))
      }
      const res = await ApiUpdateBriefDisplayOrder(senddata)
      // const res = await ApiUpdateBrief(briefs.map(item => ({
      // ...item,
      // type: 'business',
      // })));
      if (res?.success || res?.status) {
        setIsChangeOrder(false)
        fetchBriefs()
      }
    } catch (error: any) {
      toast.error(error?.message || "Something wnet wrong")
    }
  }

  return (
    <Box className="container-fluid p-4 extra" bgcolor="#F7F5F0" minHeight="calc(100vh - 180px)">
      {/* Back to Manage Business */}
      <Box className='d-flex align-items-center justify-content-between'>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/manage-store')}
          sx={{
            color: '#75724C',
            textTransform: 'none',
            fontWeight: 'bold',
            mb: 3,
            '&:hover': { bgcolor: 'transparent', opacity: 0.8 }
          }}
        >
          Back to Business
        </Button>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={enableNewBrief}
                onChange={handleOpenNewBrief}
                color="primary"
              />
            }
            label="Enable NewBrief ?"
          />
        </Box>
      </Box>

      {/* Main Title Card */}
      <Card
        elevation={0}
        sx={{
          border: '1px solid #EBE8DF',
          borderRadius: '20px',
          p: 3,
          mb: 4,
          bgcolor: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#75724C',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              display: 'block',
              mb: 0.5
            }}
          >
            Brief Main Title
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: mainTitle ? '#1A1A1A' : '#75724C80',
              fontStyle: mainTitle ? 'normal' : 'italic',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              fontSize: '18px'
            }}
          >
            {mainTitle || 'No main title set for this brief.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => {
            setMainTitleInput(mainTitle);
            setOpenMainTitleDialog(true);
          }}
          sx={{
            textTransform: 'none',
            borderRadius: '20px',
            px: 3,
            py: 1,
            fontWeight: 'bold',
            bgcolor: 'black',
            color: 'white',
            '&:hover': { bgcolor: '#222' }
          }}
        >
          {mainTitle ? 'Edit Title' : '+ Add Title'}
        </Button>
      </Card>

      {/* Header Info */}
      <Box className="d-flex mb-4 align-items-start flex-wrap justify-content-between" gap={2}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
            BUSINESS ({businessName || 'Business'})
          </Typography>
          <Typography className="fw-bold" sx={{ mb: 1 }} variant="h3">
            {briefs.length} {briefs.length === 1 ? 'selected' : 'selected'}
          </Typography>
        </Box>
        <Box className='d-flex align-items-center' gap={1}>
          <Button
            variant="contained"
            onClick={() => setOpenAddDialog(true)}
            className="table-row-background link-btn"
            sx={{
              textTransform: 'none',
              // borderRadius: '20px',
              px: 3.5,
              py: 1,
              fontWeight: 'bold',
              bgcolor: 'black',
              color: 'white',
              '&:hover': { bgcolor: '#222' }
            }}
          >
            + Add section
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (isChangeOrder) {
                saveDispalyOrder()
              }
              else {
                setIsChangeOrder(true)
              }
            }
            }
            className="table-row-background link-btn"
            sx={{
              textTransform: 'none',
              px: 3.5,
              py: 1,
              fontWeight: 'bold',
              bgcolor: 'black',
              color: 'white',
              '&:hover': { bgcolor: '#222' }
            }}
          >
            {isChangeOrder ? 'Save Order' : "Change Order"}
          </Button>
         {isChangeOrder && ( <Button
            variant="outlined"
            onClick={() => {
                setIsChangeOrder(false)
                fetchBriefs()
            }
            }
            // className="table-row-background link-btn"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              color: '#75724C',
              border:'1px solid #75724C'
              // '&:hover': { bgcolor: '#222' }
            }}
          >
            Cancel Order Changes
          </Button>
          )}
        </Box>
      </Box>

      {/* Briefs Stack list */}
      <Stack spacing={2} sx={{ maxWidth: '1100px', mx: 'auto', mt: 4 }}>
        {briefs.length > 0 ? (
          briefs?.sort((a, b) => a.displayOrder - b.displayOrder).map((item, index) => {
            const displayNum = String(index + 1).padStart(2, '0');
            const briefTitle = item.briefTitleId;
            const descriptionToShow = item.isNeedToUseDefaultDescription
              ? briefTitle?.defaultDescription
              : item.briefDescription;

            return (
              <Card
                key={item.businessBriefId}
                id={`brief-${item?.businessBriefId}`}
                // className='main-card`
                elevation={0}
                sx={{
                  border: '1px solid #EBE8DF',
                  borderRadius: '20px',
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'white',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
                  }
                }}
              >
                {/* Index Number */}
                {/* <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    mr: 2.5,
                    minWidth: '24px',
                    border: 'none !important'
                  }}
                >
                  {displayNum}
                </Typography> */}
                <TextField
                  // sx={{padid÷}}
                  className='p-0 brief-number mr-2'
                  value={localValues[item.businessBriefId] ?? displayNum ?? index + 1} // show 1-based value
                  disabled={!isChangeOrder}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: !isChangeOrder ? 'none !important' : "",
                    },
                    'input': {
                      padding: '0px',
                      minWidth:'30px'
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                  onChange={(e) => {
                    setLocalValues(prev => ({
                      ...prev,
                      [item.businessBriefId]: e.target.value
                    }));
                  }}
                  onBlur={(e) => {
                    const val = Number(e.target.value);
                    if (!isNaN(val) && val >= 1) {
                      handleOrderChange(item?.businessBriefId, val);
                    }
                    // Clear local value after update
                    setLocalValues(prev => {
                      const newValues = { ...prev };
                      delete newValues[item.businessBriefId];
                      return newValues;
                    });
                  }} inputProps={{
                    min: 1,
                    max: briefs.length,
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                      padding: '5px !important',
                      // border:'none'
                    },
                  }}
                  type='number'
                />

                {/* Circular Icon Preview */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: briefTitle?.toneColor || '#F5F4F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2.5,
                    flexShrink: 0,
                    border: '1px solid #0000001A'
                  }}
                >
                  {briefTitle?.briefIconId?.iconImage ? (
                    <Box
                      component="img"
                      src={briefTitle.briefIconId.iconImage}
                      alt={briefTitle.briefIconId.iconName}
                      sx={{
                        width: 28,
                        height: 28,
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                      None
                    </Typography>
                  )}
                </Box>

                {/* Text Content */}
                <Box sx={{ flex: 1, minWidth: 0, pr: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#75724C',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.25,
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    {briefTitle?.kicker || 'SECTION'}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#1A1A1A',
                      fontSize: '15px',
                      lineHeight: 1.3,
                      mb: 0.5,
                      fontFamily: 'inherit',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    {briefTitle?.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '12.5px',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    {stripHtml(descriptionToShow)}
                  </Typography>
                </Box>

                {/* Action Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    size="small"
                    sx={{ color: '#75724C', '&:hover': { bgcolor: '#F5F4F0' } }}
                  >
                    <ArrowUpwardIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleMoveDown(index)}
                    disabled={index === briefs.length - 1}
                    size="small"
                    sx={{ color: '#75724C', '&:hover': { bgcolor: '#F5F4F0' } }}
                  >
                    <ArrowDownwardIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleEditClick(item)}
                    size="small"
                    sx={{ color: '#75724C', '&:hover': { bgcolor: '#F5F4F0' } }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteClick(item.businessBriefId)}
                    size="small"
                    sx={{ color: 'error.main', '&:hover': { bgcolor: '#FFEBEE' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Card>
            );
          })
        ) : (
          <Box sx={{ bgcolor: 'white', border: '1px solid #EBE8DF', borderRadius: '20px', p: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No sections selected yet for this business. Click "+ Add section" to add one.
            </Typography>
          </Box>
        )}
        <Typography variant='caption'><b>Note:</b> Please click on Save Order to save brief order</Typography> 
      </Stack>

      {clientEntityId && <QuizSection type="business" id={Number(clientEntityId)} />}

      {/* Add Title Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            Add Title to Business
          </Typography>
          <IconButton onClick={() => setOpenAddDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto', p: 3 }}>
          {availableTitles.length > 0 ? (
            <Grid container spacing={2}>
              {availableTitles.map((title) => (
                <Grid item xs={12} sm={6} key={title.briefTitleId}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid #EBE8DF',
                      borderRadius: '12px',
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      bgcolor: '#F9F9F8',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'white',
                        borderColor: '#75724C',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.03)'
                      }
                    }}
                    onClick={() => handleAddBriefTitle(title.briefTitleId)}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: title.toneColor || '#F5F4F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0,
                        border: '1px solid #0000001A'
                      }}
                    >
                      {title.briefIconId?.iconImage ? (
                        <Box
                          component="img"
                          src={title.briefIconId.iconImage}
                          alt={title.briefIconId.iconName}
                          sx={{ width: 24, height: 24, objectFit: 'contain' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '9px' }}>
                          None
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      {title.kicker && (
                        <Typography variant="caption" color="#75724C" sx={{ fontWeight: 600, display: 'block', textTransform: 'uppercase' }} noWrap>
                          {title.kicker}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A1A1A' }} noWrap>
                        {title.title}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 4 }}>
              All sections in the library have already been added to this business.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button variant="outlined" onClick={() => setOpenAddDialog(false)} sx={{ color: '#75724C', borderColor: '#75724C', textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Description Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>
              Edit Title Details
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              {selectedBrief?.briefTitleId?.title}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenEditDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent dividers sx={{ maxHeight: '65vh', overflowY: 'auto', p: 3 }}>
            <Stack spacing={3}>
              {/* Checkbox to toggle description */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.isNeedToUseDefaultDescription}
                      onChange={(e) => {
                        formik.setFieldValue('isNeedToUseDefaultDescription', e.target.checked);
                      }}
                      sx={{
                        color: '#75724C',
                        '&.Mui-checked': { color: '#75724C' }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Use default library description
                    </Typography>
                  }
                />

                {/* Suggestion Notification Box */}
                <Box
                  sx={{
                    bgcolor: '#EAE8E21A',
                    border: '1px dashed #75724C33',
                    borderRadius: '8px',
                    p: 2,
                    mt: 1.5,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start'
                  }}
                >
                  <InfoIcon sx={{ color: '#75724C', mt: 0.25 }} />
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#75724C', display: 'block', mb: 0.25 }}>
                      SUGGESTION
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px', lineHeight: 1.4 }}>
                      Enabling this option pulls the standard, pre-defined default description directly from the Brief Titles library. Disable it if you need to override the description and write custom instructions tailored specifically for this business.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Description Preview or Editor */}
              {formik.values.isNeedToUseDefaultDescription ? (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                    Default Library Description Preview
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: '#F9F9F8',
                      border: '1px solid #EBE8DF',
                      borderRadius: '10px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      fontSize: '14px',
                      lineHeight: 1.5
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedBrief?.briefTitleId?.defaultDescription || '' }}
                  />
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
                      Custom Brief Description *
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        const defDesc = selectedBrief?.briefTitleId?.defaultDescription || '';
                        handleEditorValueChange(defDesc);
                      }}
                      sx={{
                        color: '#75724C',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        '&:hover': { bgcolor: 'transparent', opacity: 0.8 }
                      }}
                    >
                      Fill default description
                    </Button>
                  </Box>
                  <Box sx={{ border: '1px solid #EBE8DF', borderRadius: '10px', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
                    <PatchedReactQuill
                      setEditorValue={handleEditorValueChange}
                      editorValue={editorVal}
                      isUpdate={false}
                    />
                  </Box>
                  {formik.touched.briefDescription && formik.errors.briefDescription && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {formik.errors.briefDescription}
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button variant="outlined" onClick={() => setOpenEditDialog(false)} sx={{ color: '#75724C', borderColor: '#75724C', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" className="table-row-background" sx={{ textTransform: 'none' }}>
              Update
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Main Title Dialog */}
      <Dialog open={openMainTitleDialog} onClose={() => setOpenMainTitleDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            {mainTitle ? 'Edit Brief Main Title' : 'Add Brief Main Title'}
          </Typography>
          <IconButton onClick={() => setOpenMainTitleDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ py: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', display: 'block', mb: 1 }}>
              Brief Main Title *
            </Typography>
            <TextField
              fullWidth
              value={mainTitleInput}
              onChange={(e) => setMainTitleInput(e.target.value)}
              placeholder="Enter brief main title..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button variant="outlined" onClick={() => setOpenMainTitleDialog(false)} sx={{ color: '#75724C', borderColor: '#75724C', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveMainTitle}
            variant="contained"
            className="table-row-background"
            disabled={!mainTitleInput.trim()}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessBriefList;
